import L from 'leaflet';
import type { GlitchIGU } from '../types/index.ts';
import {
  ARCGIS_BASEMAPS,
  type ArcgisBasemapType,
  getWaybackUrl
} from '../services/arcgis.ts';

let mapInstance: L.Map | null = null;
let currentBasemapLayer: L.TileLayer | null = null;
let labelsReferenceLayer: L.TileLayer | null = null;
let markersLayer: L.LayerGroup | null = null;

let activeBasemapType: ArcgisBasemapType = 'imagery'; // Por defecto Satélite HD para máxima nitidez global
let activeWaybackYear: string = '2026';
let activeTerritory: string = 'El Raval';

const TERRITORIOS: Record<string, { coords: [number, number]; zoom: number; desc: string }> = {
  'El Raval': {
    coords: [41.3833, 2.1670],
    zoom: 15,
    desc: 'Ciutat Vella, Barcelona, España'
  },
  'Barrio Yungay': {
    coords: [-33.4417, -70.6761],
    zoom: 15,
    desc: 'Santiago Centro, Chile'
  },
  'Gamcheon Culture Village': {
    coords: [35.0975, 129.0106],
    zoom: 16,
    desc: 'Saha-gu, Busan, Corea del Sur'
  }
};

export function setArcgisBasemap(type: ArcgisBasemapType) {
  if (!mapInstance) return;

  // Detener cualquier animación previa para evitar colisiones en Leaflet
  mapInstance.stop();

  if (currentBasemapLayer) {
    try {
      mapInstance.removeLayer(currentBasemapLayer);
    } catch (e) {
      // Ignorar si ya fue removido
    }
    currentBasemapLayer = null;
  }
  if (labelsReferenceLayer) {
    try {
      mapInstance.removeLayer(labelsReferenceLayer);
    } catch (e) {
      // Ignorar si ya fue removido
    }
    labelsReferenceLayer = null;
  }

  activeBasemapType = type;

  if (type === 'wayback') {
    // Cargar capa histórica ArcGIS Wayback para el año seleccionado
    const url = getWaybackUrl(activeWaybackYear);
    currentBasemapLayer = L.tileLayer(url, {
      maxZoom: 19,
      attribution: 'Esri ArcGIS Wayback Imagery'
    }).addTo(mapInstance);
  } else {
    const config = ARCGIS_BASEMAPS[type] || ARCGIS_BASEMAPS.imagery;
    currentBasemapLayer = L.tileLayer(config.url, {
      attribution: config.attribution,
      maxZoom: config.maxZoom
    }).addTo(mapInstance);

    // En modo híbrido, superponer etiquetas de nombres y calles
    if (type === 'hybrid') {
      labelsReferenceLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Esri Reference'
      }).addTo(mapInstance);
    }
  }

  if (currentBasemapLayer) {
    (currentBasemapLayer as any).bringToBack?.();
  }

  if (markersLayer) {
    markersLayer.eachLayer(layer => {
      (layer as any).bringToFront?.();
    });
  }

  // Actualizar clases activas en los botones de capas
  document.querySelectorAll('button[data-arcgis-basemap]').forEach(btn => {
    if (btn.getAttribute('data-arcgis-basemap') === type) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });
}

export function setWaybackYear(year: string) {
  activeWaybackYear = year;
  setArcgisBasemap('wayback');

  // Actualizar botones de año
  document.querySelectorAll('button[data-wayback-year]').forEach(btn => {
    if (btn.getAttribute('data-wayback-year') === year) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  });

  const ind = document.getElementById('wayback-active-indicator');
  if (ind) {
    ind.innerText = `● Captura Satelital: ${year} (ArcGIS Wayback)`;
  }
}

export function flyToTerritory(terName: string) {
  if (!mapInstance || !TERRITORIOS[terName]) return;

  activeTerritory = terName;
  const { coords, zoom } = TERRITORIOS[terName];

  // En Busan, si está seleccionada la capa topográfica, cambiar a satélite para evitar restricciones de servidor
  if (terName === 'Gamcheon Culture Village' && activeBasemapType === 'topographic') {
    setArcgisBasemap('imagery');
  }

  mapInstance.stop();
  mapInstance.flyTo(coords, zoom, {
    duration: 1.2,
    easeLinearity: 0.25
  });

  setTimeout(() => {
    mapInstance?.invalidateSize();
  }, 350);

  // Actualizar botón de territorio activo
  document.querySelectorAll('button[data-territory]').forEach(b => {
    if (b.getAttribute('data-territory') === terName) {
      b.classList.add('active');
    } else {
      b.classList.remove('active');
    }
  });
}

function apply3DTilt(angle: number) {
  const wrapper = document.getElementById('map-viewport-wrapper');
  const mapEl = document.getElementById('doctoral-map');
  const hudControls = document.getElementById('hud-3d-controls');

  if (!wrapper || !mapEl) return;

  if (angle > 0) {
    wrapper.classList.add('is-3d-active');
    mapEl.style.transform = `rotateX(${angle}deg) scale(1.18)`;
    if (hudControls) hudControls.style.display = 'flex';
  } else {
    wrapper.classList.remove('is-3d-active');
    mapEl.style.transform = 'none';
    if (hudControls) hudControls.style.display = 'none';
  }

  setTimeout(() => {
    mapInstance?.invalidateSize();
  }, 200);
}

export function initAtlas() {
  const mapElement = document.getElementById('doctoral-map');
  if (!mapElement || mapInstance) return;

  // Centro inicial: El Raval
  mapInstance = L.map('doctoral-map', {
    zoomControl: true,
    scrollWheelZoom: true
  }).setView([41.3833, 2.1670], 15);

  // Inicializar con Satélite HD de Esri ArcGIS
  setArcgisBasemap(activeBasemapType);

  markersLayer = L.layerGroup().addTo(mapInstance);

  // Selector de capas ArcGIS
  document.querySelectorAll('button[data-arcgis-basemap]').forEach(btn => {
    btn.addEventListener('click', () => {
      const basemapType = btn.getAttribute('data-arcgis-basemap') as ArcgisBasemapType;
      if (basemapType) {
        setArcgisBasemap(basemapType);
      }
    });
  });

  // Selector de Años (Wayback Timeline)
  document.querySelectorAll('button[data-wayback-year]').forEach(btn => {
    btn.addEventListener('click', () => {
      const year = btn.getAttribute('data-wayback-year');
      if (year) {
        setWaybackYear(year);
      }
    });
  });

  // Botones para volar a territorios de estudio
  document.querySelectorAll('button[data-territory]').forEach(btn => {
    btn.addEventListener('click', () => {
      const terName = btn.getAttribute('data-territory');
      if (terName) {
        flyToTerritory(terName);
      }
    });
  });

  // Alternador de Dimensión 2D / 3D
  const btn2D = document.getElementById('btn-toggle-2d');
  const btn3D = document.getElementById('btn-toggle-3d');

  btn2D?.addEventListener('click', () => {
    btn2D.classList.add('active');
    btn3D?.classList.remove('active');
    apply3DTilt(0);
  });

  btn3D?.addEventListener('click', () => {
    btn3D.classList.add('active');
    btn2D?.classList.remove('active');
    apply3DTilt(50);
  });

  // Controles de inclinación en el HUD 3D
  document.getElementById('btn-tilt-low')?.addEventListener('click', (e) => {
    updateHudTiltButton(e.currentTarget as HTMLElement);
    apply3DTilt(30);
  });

  document.getElementById('btn-tilt-mid')?.addEventListener('click', (e) => {
    updateHudTiltButton(e.currentTarget as HTMLElement);
    apply3DTilt(50);
  });

  document.getElementById('btn-tilt-high')?.addEventListener('click', (e) => {
    updateHudTiltButton(e.currentTarget as HTMLElement);
    apply3DTilt(65);
  });

  document.getElementById('btn-rot-reset')?.addEventListener('click', () => {
    apply3DTilt(50);
    const ter = TERRITORIOS[activeTerritory];
    if (ter && mapInstance) {
      mapInstance.setView(ter.coords, ter.zoom);
    }
  });
}

function updateHudTiltButton(target: HTMLElement) {
  document.querySelectorAll('.btn-hud-3d').forEach(b => b.classList.remove('active'));
  target.classList.add('active');
}

export function updateAtlasMarkers(glitches: GlitchIGU[]) {
  if (!mapInstance || !markersLayer) return;

  markersLayer.clearLayers();

  // Marcadores de referencia territorial base
  Object.entries(TERRITORIOS).forEach(([nombre, data]) => {
    const iconBase = L.divIcon({
      className: 'custom-pin-base',
      html: `<div style="background:var(--strawbs, #E63F25); color:#fff; font-size:10px; font-weight:700; padding:3px 7px; border-radius:4px; border:1.5px solid #fff; white-space:nowrap; box-shadow:0 2px 6px rgba(0,0,0,0.35); font-family:var(--font-sans);">${nombre}</div>`,
      iconSize: [80, 22],
      iconAnchor: [40, 11]
    });
    L.marker(data.coords, { icon: iconBase })
      .bindPopup(`<strong>${nombre}</strong><br><small>${data.desc}</small>`)
      .addTo(markersLayer!);
  });

  // Marcadores de Glitches registrados en SQLite
  glitches.forEach((g, idx) => {
    const terKey = g.caso_estudio || g.caso_nombre || 'El Raval';
    const ter = TERRITORIOS[terKey];
    const baseCoords = ter ? ter.coords : [41.3833, 2.1670];
    const offset = ((idx % 5) + 1) * 0.0011;

    // Si tiene coordenadas georreferenciadas reales en SQLite las usamos
    const lat = (g.latitud && g.latitud !== 0) ? g.latitud : (baseCoords[0] + (idx % 2 === 0 ? offset : -offset));
    const lng = (g.longitud && g.longitud !== 0) ? g.longitud : (baseCoords[1] + (idx % 3 === 0 ? offset : -offset));

    const iguVal = g.igu ?? g.indice_igu ?? 0;
    const fillColor = iguVal >= 3.0 ? '#E63F25' : (iguVal >= 2.0 ? '#FFCB5D' : '#799E60');

    const marker = L.circleMarker([lat, lng], {
      radius: 9,
      fillColor,
      color: '#ffffff',
      weight: 2,
      opacity: 1,
      fillOpacity: 0.92
    });

    marker.bindPopup(`
      <div style="font-family:system-ui; font-size:12px; line-height:1.45; padding:2px;">
        <strong style="color:var(--strawbs, #E63F25); font-size:13px;">${escapeHtml(terKey)}</strong><br>
        <b>Estación:</b> ${escapeHtml(g.estacion || g.name || '')}<br>
        <b>Dimensión:</b> ${escapeHtml(g.dimension || '')}<br>
        <b>Índice IGU:</b> <span style="font-weight:700; color:${fillColor}; background:#fff3f3; padding:1px 5px; border-radius:3px;">${iguVal.toFixed(2)}</span><br>
        <p style="margin-top:5px; color:#4A2624;">${escapeHtml(g.descripcion || g.desc_fenomenologica || '')}</p>
      </div>
    `);

    markersLayer!.addLayer(marker);
  });
}

export function refreshMapLayout() {
  if (mapInstance) {
    setTimeout(() => {
      mapInstance!.invalidateSize();
    }, 200);
  }
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.innerText = str;
  return div.innerHTML;
}
